import React from "react";
import { useNavigate } from "react-router-dom";
import { Plus, EllipsisVertical } from "lucide-react";
import {
  RequestColumnKey,
  RequestsTable,
} from "../../../requests/components/RequestsTable";
import { Button } from "@/components/ui/button";
import CreateRequestModal from "@/components/pages/requests/components/modals/CreateRequestModal";
import { Request, RequestType } from "@/types/request";
import { ToggleButton } from "@/components/pages/reservations/components/reservationDetails/ToggleButton";
import { useToggle } from "@/hooks/useToggle";
import { useTranslation } from "react-i18next";

interface RequestsInfoProps {
  bookingId: string;
  guestId?: string;
  roomId?: string;
  initialGuestDisplay?: string;
  initialReservationDisplay?: string;
  initialRoomDisplay?: string;
  guestRequests: Request[];
  roomRequests: Request[];
  stub: () => void;
}

const requestsColumns: RequestColumnKey[] = [
  "service",
  "createdAt",
  "dueDateTime",
  "status",
  "assignedStaff",
  "createdBy",
  "paymentStatus",
  "actions",
];

const roomServiceLogsColumns: RequestColumnKey[] = [
  "priority",
  "status",
  "createdAt",
  "dueDateTime",
  "assignedStaff",
  "createdBy",
  "actions",
];

export const RequestsInfo: React.FC<RequestsInfoProps> = ({
  bookingId,
  guestId,
  roomId,
  initialGuestDisplay,
  initialReservationDisplay,
  initialRoomDisplay,
  guestRequests,
  roomRequests,
  stub,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    value: isRoomServiceLogsOpened,
    handleToggle: handleRoomServiceLogsToggle,
  } = useToggle(true);

  return (
    <>
      <div className="flex items-center justify-between">
        <h2 className="text-violet pl-4 text-sm font-normal">
          {t("reservation.table.requests")}
        </h2>

        <CreateRequestModal
          requestType={RequestType.GUEST}
          bookingId={bookingId}
          guestId={guestId}
          roomId={roomId}
          initialGuestDisplay={initialGuestDisplay}
          initialReservationDisplay={initialReservationDisplay}
          initialRoomDisplay={initialRoomDisplay}
          triggerNode={
            <Button type="button" variant="outline" className="!px-4">
              {t("requests.page.addRequest")}
              <Plus className="h-4 w-4" />
            </Button>
          }
        />
      </div>

      {guestRequests?.length > 0 && (
        <RequestsTable
          requests={guestRequests}
          visibleColumns={requestsColumns}
          isLoading={false}
          withPagination={false}
          withSorting={false}
          onRowClick={(row: Request) => navigate(`/requests/${row.id}`)}
          onEditRequest={() => navigate("/requests")}
          onUpdateRequest={() => {}}
          selection={{
            selectAll: false,
            toggleSelectAll: stub,
            toggleSelectItem: stub,
            selectedItems: [],
            isSelected: () => false,
            clearSelection: stub,
            selectedCount: 0,
          }}
          serviceCellClassName="flex h-10 p-1 items-center justify-center bg-light-indigo rounded-lg text-indigo text-[10px] font-semibold"
          dataCellClassName="gap-0"
          statusCellClassName="w-fit data-[size=default]:h-6 data-[size=sm]:h-6 p-2 bg-slate border-0 text-light-zinc text-xs font-medium"
          statusCellIconClassName="text-light-zinc border-l border-white pl-1 size-5.5"
          assignedStaffClassName="flex-row justify-center"
          teamClassName="text-[10px]"
          staffClassName="text-[10px]"
          assignedStaffIconSize={8}
          assignedStaffShowCircle={false}
          notAssignedStaffClassName="text-xs rounded-lg border border-neutral-200 px-4 py-1"
          actionsEditIcon={<EllipsisVertical className="text-indigo h-4 w-4" />}
          isChatWithGuest={true}
          showChatAction={true}
          createdByClassName="font-medim text-[10px]"
        />
      )}

      {roomRequests?.length > 0 && (
        <>
          <ToggleButton
            title={t("requests.page.roomServiceLogs")}
            isOpened={isRoomServiceLogsOpened}
            handleToggle={handleRoomServiceLogsToggle}
          />

          {isRoomServiceLogsOpened && (
            <RequestsTable
              requests={roomRequests}
              visibleColumns={roomServiceLogsColumns}
              isLoading={false}
              withPagination={false}
              withSorting={false}
              onRowClick={(row: Request) => navigate(`/requests/${row.id}`)}
              onEditRequest={() => navigate("/requests")}
              onUpdateRequest={() => {}}
              selection={{
                selectAll: false,
                toggleSelectAll: stub,
                toggleSelectItem: stub,
                selectedItems: [],
                isSelected: () => false,
                clearSelection: stub,
                selectedCount: 0,
              }}
              dataCellClassName="gap-0"
              assignedStaffClassName="flex-row justify-center"
              teamClassName="text-[10px]"
              staffClassName="text-[10px]"
              assignedStaffIconSize={8}
              assignedStaffShowCircle={false}
              notAssignedStaffClassName="text-xs rounded-lg border border-neutral-200 px-4 py-1"
              actionsEditIcon={
                <EllipsisVertical className="text-indigo h-4 w-4" />
              }
              isChatWithGuest={false}
              showChatAction={true}
              createdByClassName="font-medim text-[10px]"
              starClassName="w-4 h-4"
              priorityStarClassName="fill-yellow-500 text-yellow-500"
              applyStatusBadge={true}
              applyPaymentStatusBadge={true}
              showStatusIcon={false}
              statusCellClassName="w-fit data-[size=default]:h-6 data-[size=sm]:h-6 border-0"
            />
          )}
        </>
      )}
    </>
  );
};
